import { useId } from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { GrabberIcon } from "@primer/octicons-react";
import "./SortableList.css";

export function SortableList({ after, className = "", disabled = false, getId, getLabel, itemClassName = "", items, onMove, preserveHandleSpace = false, renderItem }) {
  const listId = useId();

  function renderItems() {
    return items.map((item, index) => {
      const id = getId(item);
      const resolvedItemClassName = typeof itemClassName === "function" ? itemClassName(item, index) : itemClassName;
      return disabled ? (
        <SortableItem className={resolvedItemClassName} disabled key={id} preserveHandleSpace={preserveHandleSpace}>
          {renderItem(item, index)}
        </SortableItem>
      ) : (
        <Draggable draggableId={String(id)} index={index} key={id}>
          {(provided, snapshot) => (
            <SortableItem
              className={resolvedItemClassName}
              dragHandleProps={provided.dragHandleProps}
              dragging={snapshot.isDragging}
              index={index}
              innerRef={provided.innerRef}
              label={getLabel?.(item, index) ?? String(id)}
              onKeyboardMove={(nextIndex) => onMove(index, nextIndex)}
              style={provided.draggableProps.style}
              total={items.length}
              {...provided.draggableProps}
            >
              {renderItem(item, index)}
            </SortableItem>
          )}
        </Draggable>
      );
    });
  }

  if (disabled) return <div className={`sortable-list ${className}`.trim()}>{renderItems()}{after ? <div className="sortable-slot">{after}</div> : null}</div>;

  return (
    <DragDropContext onDragEnd={({ destination, source }) => {
      const nextIndex = moveDestination(source, destination);
      if (nextIndex != null) onMove(source.index, nextIndex);
    }}>
      <Droppable droppableId={listId} direction="vertical">
        {(provided) => (
          <div className={`sortable-list ${className}`.trim()} ref={provided.innerRef} {...provided.droppableProps}>
            {renderItems()}
            {after ? <div className="sortable-slot">{after}</div> : null}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export function moveDestination(source, destination) {
  return destination && destination.index !== source.index ? destination.index : null;
}

function SortableItem({ children, className, disabled = false, dragHandleProps, dragging = false, index, innerRef, label, onKeyboardMove, preserveHandleSpace = false, style, total, ...draggableProps }) {
  function handleKeyDown(event) {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
      dragHandleProps.onKeyDown?.(event);
      return;
    }
    event.preventDefault();
    const nextIndex = event.key === "ArrowUp" ? index - 1 : index + 1;
    if (nextIndex >= 0 && nextIndex < total) onKeyboardMove(nextIndex);
  }

  return (
    <div className="sortable-slot" ref={innerRef} style={style} {...draggableProps}>
      <div className={`sortable-item${disabled ? " disabled" : ""}${preserveHandleSpace ? " preserve-handle-space" : ""}${dragging ? " dragging" : ""}${className ? ` ${className}` : ""}`}>
        <div className="sortable-content">{children}</div>
        {disabled ? preserveHandleSpace ? <span className="sortable-handle-space" aria-hidden="true" /> : null : (
          <button className="sortable-handle" type="button" aria-label={`Reorder ${label}`} {...dragHandleProps} onKeyDown={handleKeyDown}>
            <GrabberIcon />
          </button>
        )}
      </div>
    </div>
  );
}
